import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Peer from "simple-peer";
import { toast } from "react-toastify";
import { communicationApi } from "./communicationApi";
import { connectCommunicationHub, dropPeer, invokeHub, peerFor, trackPeer } from "./communicationHub";

const defaultSettings = {
  isChatEnabled: true,
  isVoiceCallEnabled: true,
  isVideoCallEnabled: true,
  isOneToOneCallEnabled: true,
  isGroupCallEnabled: false,
  maxGroupCallParticipants: 4,
};

const flag = (row, name) => {
  const value = row?.[name] ?? row?.[name[0].toUpperCase() + name.slice(1)];
  return value === true || value === 1 || value === "true";
};

const CommunicationModule = () => {
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const myId = Number(user?.id || 0);
  const siteId = Number(user?.siteId || 0);
  const [settings, setSettings] = useState(defaultSettings);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [people, setPeople] = useState([]);
  const [peopleQuery, setPeopleQuery] = useState("");
  const [directUserId, setDirectUserId] = useState("");
  const [groupTitle, setGroupTitle] = useState("");
  const [groupUserIds, setGroupUserIds] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [call, setCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [tab, setTab] = useState("chat");

  const memberCount = Number(active?.MemberCount || active?.memberCount || 0);
  const canVoice = settings.isVoiceCallEnabled && ((memberCount <= 2 && settings.isOneToOneCallEnabled) || (memberCount > 2 && settings.isGroupCallEnabled));
  const canVideo = settings.isVideoCallEnabled && canVoice;

  const load = async () => {
    if (!siteId) return;
    const settingsRes = await communicationApi.settings(siteId);
    const settingsRow = settingsRes.data?.data?.[0] || {};
    setSettings({
      isChatEnabled: flag(settingsRow, "isChatEnabled"),
      isVoiceCallEnabled: flag(settingsRow, "isVoiceCallEnabled"),
      isVideoCallEnabled: flag(settingsRow, "isVideoCallEnabled"),
      isOneToOneCallEnabled: flag(settingsRow, "isOneToOneCallEnabled"),
      isGroupCallEnabled: flag(settingsRow, "isGroupCallEnabled"),
      maxGroupCallParticipants: Number(settingsRow.MaxGroupCallParticipants || settingsRow.maxGroupCallParticipants || 4),
    });
    const list = await communicationApi.listConversations(siteId);
    setConversations(list.data?.data || []);
    const peopleRes = await communicationApi.listSitePeople(siteId);
    const body = peopleRes.data || {};
    const rows = body.Commuserdetailsdata || body.commuserdetailsdata || body.data || [];
    setPeople((Array.isArray(rows) ? rows : []).filter((person) => Number(person.Id || person.id) !== myId));
  };

  useEffect(() => {
    load();
    if (!token) return undefined;
    connectCommunicationHub(token, {
      ReceiveMessage: () => active && openThread(active),
      IncomingCall: (payload) => setCall({ ...payload, incoming: true }),
      ReceiveOffer: (payload) => acceptSignal(payload, false),
      ReceiveAnswer: (payload) => peerFor(payload.fromUserId)?.signal({ type: "answer", sdp: payload.sdp }),
      ReceiveIceCandidate: (payload) => peerFor(payload.fromUserId)?.signal({ candidate: JSON.parse(payload.candidate) }),
      CallEnded: () => hangUp(false),
      SessionRevoked: () => hangUp(false),
      FeatureDisabled: () => hangUp(false),
    }).catch((error) => toast.error(error.message));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, siteId]);

  const openThread = async (conversation) => {
    setActive(conversation);
    const id = conversation.ConversationId || conversation.conversationId;
    await invokeHub("JoinConversation", id);
    const res = await communicationApi.listMessages(id);
    setMessages((res.data?.data || []).slice().reverse());
    const members = await communicationApi.listMembers(id);
    setMemberIds((members.data?.data || []).map((row) => Number(row.UserId || row.userId)).filter(Boolean));
  };

  const personName = (person) => `${person.FirstName || person.firstName || ""} ${person.LastName || person.lastName || ""}`.trim() || "Unnamed";
  const personUnit = (person) => person.unitNumber || person.UnitNumber || person.unitName || person.UnitName || "";
  const personId = (person) => Number(person.Id || person.id);
  const filteredPeople = people.filter((person) => {
    const haystack = `${personUnit(person)} ${personName(person)}`.toLowerCase();
    return haystack.includes(peopleQuery.trim().toLowerCase());
  });

  const createDirect = async () => {
    if (!directUserId) {
      toast.error("Choose a person.");
      return;
    }
    const chosen = people.find((person) => personId(person) === Number(directUserId));
    const directTitle = chosen ? `${personUnit(chosen) ? `${personUnit(chosen)} - ` : ""}${personName(chosen)}` : undefined;
    const res = await communicationApi.createConversation({ siteId, conversationType: "Direct", otherUserId: Number(directUserId), title: directTitle });
    if (res.data?.IsSuccess === "true" || res.data?.isSuccess === "true") {
      setDirectUserId("");
      await load();
    } else toast.error(res.data?.Message || res.message);
  };

  const addPeople = async (conversationId, userIds) => {
    const failures = [];
    for (const userId of userIds) {
      const added = await communicationApi.members({ conversationId, userId, memberRole: "Member" }, "add");
      if (added.data?.IsSuccess === "false") failures.push(added.data.Message);
    }
    if (failures.length) toast.error(failures[0]);
  };

  const createGroup = async () => {
    if (!groupTitle.trim()) {
      toast.error("Enter a group name.");
      return;
    }
    if (groupUserIds.length === 0) {
      toast.error("Select at least one person.");
      return;
    }
    const res = await communicationApi.createConversation({ siteId, conversationType: "Group", title: groupTitle.trim() });
    const createdId = res.data?.data?.[0]?.ConversationId;
    if ((res.data?.IsSuccess === "true" || res.data?.isSuccess === "true") && createdId) {
      await addPeople(createdId, groupUserIds);
      setGroupTitle("");
      setGroupUserIds([]);
      await load();
      toast.success("Group created");
    } else toast.error(res.data?.Message || res.message);
  };

  const addToOpenGroup = async () => {
    if (!active) return;
    const conversationId = active.ConversationId || active.conversationId;
    const pending = groupUserIds.filter((id) => !memberIds.includes(id));
    if (pending.length === 0) {
      toast.error("Select people who are not already in this group.");
      return;
    }
    await addPeople(conversationId, pending);
    setGroupUserIds([]);
    await openThread(active);
  };

  const toggleGroupUser = (id) => {
    setGroupUserIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const send = async () => {
    if (!draft.trim() || !active) return;
    const conversationId = active.ConversationId || active.conversationId;
    const res = await communicationApi.sendMessage({ conversationId, messageType: "Text", messageText: draft });
    if (res.data?.IsSuccess === "false") toast.error(res.data?.Message);
    setDraft("");
    await openThread(active);
  };

  const iceServers = (payload) => {
    const servers = [];
    (payload.stunUrls || []).forEach((urls) => servers.push({ urls }));
    if (payload.turnUrls?.length) {
      servers.push({ urls: payload.turnUrls, username: payload.turnUsername, credential: payload.turnCredential });
    }
    return servers.length ? servers : [{ urls: "stun:stun.l.google.com:19302" }];
  };

  const mediaFor = async (mediaType) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: mediaType === "Video",
    });
    setLocalStream(stream);
    return stream;
  };

  const makePeer = (targetUserId, initiator, stream, payload) => {
    const peer = new Peer({
      initiator,
      trickle: true,
      stream,
      config: { iceServers: iceServers(payload) },
    });
    peer.on("signal", (signal) => {
      if (signal.type === "offer") invokeHub("SendOffer", payload.callSessionId, targetUserId, signal.sdp);
      else if (signal.type === "answer") invokeHub("SendAnswer", payload.callSessionId, targetUserId, signal.sdp);
      else if (signal.candidate) invokeHub("SendIceCandidate", payload.callSessionId, targetUserId, JSON.stringify(signal.candidate));
    });
    peer.on("stream", (remote) => {
      const audio = document.getElementById(`remote-${targetUserId}`);
      if (audio) audio.srcObject = remote;
    });
    trackPeer(targetUserId, peer);
    return peer;
  };

  const startCall = async (mediaType) => {
    if (!active) return;
    const conversationId = active.ConversationId || active.conversationId;
    try {
      const stream = await mediaFor(mediaType);
      const res = await communicationApi.startCall({ conversationId, mediaType });
      const body = res.data || {};
      if (body.IsSuccess !== "true") {
        toast.error(body.Message || "Call was not started");
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      setCall(body);
      await invokeHub("AcceptCall", body.callSessionId);
    } catch (error) {
      toast.error(error.message || "Microphone or camera permission was denied");
    }
  };

  const acceptSignal = async (payload, initiator) => {
    let stream = localStream;
    if (!stream) stream = await mediaFor(payload.mediaType || "Voice");
    const peer = peerFor(payload.fromUserId) || makePeer(payload.fromUserId, initiator || myId < payload.fromUserId, stream, payload);
    if (payload.sdp) peer.signal({ type: initiator ? "offer" : "offer", sdp: payload.sdp });
  };

  const answer = async () => {
    if (!call) return;
    try {
      const stream = await mediaFor(call.mediaType || call.MediaType || "Voice");
      const callSessionId = call.callSessionId || call.CallSessionId;
      await invokeHub("AcceptCall", callSessionId);
      setCall({ ...call, incoming: false, callSessionId });
      const remoteId = call.startedBy || call.StartedBy;
      if (remoteId && myId < remoteId) makePeer(remoteId, true, stream, { ...call, callSessionId });
    } catch (error) {
      toast.error(error.message || "Microphone or camera permission was denied");
    }
  };

  const hangUp = async (tellServer = true) => {
    if (tellServer && call) {
      const callSessionId = call.callSessionId || call.CallSessionId;
      if (callSessionId) await communicationApi.endCall({ callSessionId, endReason: "Ended" });
    }
    if (localStream) localStream.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
    setCall(null);
    dropPeer(call?.startedBy || call?.StartedBy);
  };

  const saveSettings = async () => {
    const res = await communicationApi.saveSettings({ siteId, ...settings });
    if (res.data?.IsSuccess === "false") toast.error(res.data.Message);
    else toast.success("Communication settings saved");
    await load();
  };

  const title = useMemo(() => active?.Title || active?.title || `Conversation ${active?.ConversationId || ""}`, [active]);

  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <section className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200">
        <div className="flex gap-2 mb-3">
          <button className="px-3 py-1 rounded bg-slate-100" onClick={() => setTab("chat")}>Chat</button>
          <button className="px-3 py-1 rounded bg-slate-100" onClick={() => setTab("settings")}>Settings</button>
        </div>
        {tab === "settings" ? (
          <div className="flex flex-col gap-2 text-sm">
            {Object.entries({
              isChatEnabled: "Chat",
              isVoiceCallEnabled: "Voice",
              isVideoCallEnabled: "Video",
              isOneToOneCallEnabled: "One-to-one calls",
              isGroupCallEnabled: "Group calls",
            }).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between">
                {label}
                <input type="checkbox" checked={!!settings[key]} onChange={(event) => setSettings({ ...settings, [key]: event.target.checked })} />
              </label>
            ))}
            <label className="flex items-center justify-between">
              Max group size
              <input className="border rounded px-2 w-16" type="number" min="2" max="4" value={settings.maxGroupCallParticipants} onChange={(event) => setSettings({ ...settings, maxGroupCallParticipants: Number(event.target.value) })} />
            </label>
            <button className="mt-2 px-3 py-2 rounded bg-slate-900 text-white" onClick={saveSettings}>Save</button>
          </div>
        ) : (
          <>
            {!settings.isChatEnabled && <p className="text-sm text-red-600 mb-2">Chat is disabled for this site.</p>}
            {settings.isChatEnabled && (
              <div className="flex flex-col gap-3 mb-3 text-sm">
                <input className="border rounded px-2 py-1" placeholder="Search unit number or person name" value={peopleQuery} onChange={(event) => setPeopleQuery(event.target.value)} />
                <div>
                  <div className="font-medium mb-1">One-to-one</div>
                  <div className="flex gap-2">
                    <select className="border rounded px-2 py-1 flex-1" value={directUserId} onChange={(event) => setDirectUserId(event.target.value)}>
                      <option value="">Unit number and person</option>
                      {filteredPeople.map((person) => (
                        <option key={personId(person)} value={personId(person)}>
                          {personUnit(person) ? `${personUnit(person)} - ${personName(person)}` : personName(person)}
                        </option>
                      ))}
                    </select>
                    <button className="px-2 rounded bg-slate-900 text-white" onClick={createDirect}>Start</button>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">New group</span>
                    <button type="button" className="text-sky-700" onClick={() => setGroupUserIds(filteredPeople.map(personId))}>Select all on this site</button>
                  </div>
                  <input className="border rounded px-2 py-1 w-full mb-2" placeholder="Group name" value={groupTitle} onChange={(event) => setGroupTitle(event.target.value)} />
                  <div className="max-h-40 overflow-auto border rounded p-2">
                    {filteredPeople.length === 0 && <p className="text-slate-500">No people found for this site.</p>}
                    {filteredPeople.map((person) => {
                      const id = personId(person);
                      return (
                        <label key={id} className="flex items-center gap-2 py-1">
                          <input type="checkbox" checked={groupUserIds.includes(id)} onChange={() => toggleGroupUser(id)} />
                          <span>{personName(person)}{personUnit(person) ? ` · ${personUnit(person)}` : ""}</span>
                        </label>
                      );
                    })}
                  </div>
                  <button className="mt-2 px-3 py-1 rounded bg-slate-900 text-white" onClick={createGroup}>Create group</button>
                </div>
              </div>
            )}
            {conversations.map((item) => (
              <button key={item.ConversationId} className="block w-full text-left px-2 py-2 rounded hover:bg-slate-100" onClick={() => openThread(item)}>
                {item.Title || `${item.ConversationType} #${item.ConversationId}`}
              </button>
            ))}
          </>
        )}
      </section>
      <section className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 min-h-[420px] flex flex-col">
        {!active ? <p className="text-slate-500">Select a conversation.</p> : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-semibold">{title}</h2>
                {memberIds.length > 0 && (
                  <p className="text-xs text-slate-500">
                    {memberIds.map((id) => {
                      const person = people.find((item) => personId(item) === id);
                      if (!person && id === myId) return "You";
                      return person ? personName(person) : `User ${id}`;
                    }).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {canVoice && <button className="px-3 py-1 rounded bg-emerald-600 text-white" onClick={() => startCall("Voice")}>Voice</button>}
                {canVideo && <button className="px-3 py-1 rounded bg-sky-600 text-white" onClick={() => startCall("Video")}>Video</button>}
              </div>
            </div>
            {(active.ConversationType || active.conversationType) === "Group" && settings.isChatEnabled && (
              <button className="self-start mb-2 text-sm px-3 py-1 rounded border" onClick={addToOpenGroup}>Add selected people to this group</button>
            )}
            <div className="flex-1 overflow-auto space-y-2">
              {messages.map((message) => (
                <div key={message.MessageId} className="text-sm">
                  <span className="font-medium">{message.SenderId}: </span>{message.MessageText}
                </div>
              ))}
            </div>
            {settings.isChatEnabled && (
              <div className="flex gap-2 mt-3">
                <input className="border rounded px-2 flex-1" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Message" />
                <button className="px-3 rounded bg-slate-900 text-white" onClick={send}>Send</button>
              </div>
            )}
          </>
        )}
        {call && (
          <div className="mt-3 p-3 rounded border">
            <p className="text-sm mb-2">{call.incoming ? "Incoming" : "Active"} {call.mediaType || call.MediaType} call</p>
            <video id="local-preview" autoPlay muted playsInline ref={(node) => { if (node && localStream) node.srcObject = localStream; }} className="w-40" />
            <audio id={`remote-${call.startedBy || call.StartedBy || "remote"}`} autoPlay />
            {call.incoming && <button className="mr-2 px-3 py-1 rounded bg-emerald-600 text-white" onClick={answer}>Answer</button>}
            <button className="px-3 py-1 rounded bg-red-600 text-white" onClick={() => hangUp(true)}>End</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default CommunicationModule;
